import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChangePassword } from "@/components/ChangePassword";
import { LogoUpload } from "@/components/LogoUpload";
import { ContratoModelosConfig } from "@/components/ContratoModelosConfig";
import { MensagensConfig } from "@/components/MensagensConfig";
import { MinhaAssinatura } from "@/components/MinhaAssinatura";
import { ResponsaveisConfig } from "@/components/ResponsaveisConfig";
import { EquipeConfig } from "@/components/EquipeConfig";
import { ModelosConfig } from "@/components/ModelosConfig";
import { PushConfig } from "@/components/PushConfig";
import { AuditLog } from "@/components/AuditLog";
import { Crown, ShieldCheck, Link2, Loader2, Building2, Save } from "lucide-react";
import { toast } from "sonner";

const COMPANY_FIELDS = [
  ["razao_social", "Razão social"], ["cnpj", "CNPJ"], ["representante", "Representante / responsável"], ["cargo_responsavel", "Cargo do responsável"],
  ["endereco", "Endereço"], ["bairro", "Bairro"], ["cep", "CEP"],
  ["cidade_uf", "Cidade/UF"], ["whatsapp", "WhatsApp"], ["pix", "Chave PIX"], ["titular_pix", "Titular da chave PIX"], ["banco", "Banco"],
  ["taxa_visita", "Taxa de visita (R$)"], ["valor_deslocamento", "Valor deslocamento colaborador (R$) — informado no texto do contrato"],
  ["valor_atraso_dia", "Valor cobrado por dia em caso de atraso (R$) — informado no texto do contrato"], ["foro", "Foro"],
  ["meta_diaria", "Meta diária fixa de recebimento (R$) — vazio = usar previsto das parcelas"],
  ["meta_mensal", "Meta mensal de recebimento (R$) — barra no Dashboard; vazio = não exibir"],
  ["multa_tipo", "Multa por atraso: tipo (percentual ou fixa)"],
  ["multa_valor", "Multa: valor (% do saldo ou R$ fixo) — vazio = usar % do contrato"],
  ["juros_dia", "Juros por dia de atraso (% do saldo) — opcional"],
  ["tolerancia_min", "Tolerância após o horário de vencimento (minutos)"],
];

export default function Settings() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);
  const isAdmin = user?.role === "administrador";
  const isMaster = user?.platform_role === "superadmin";

  const loadCompany = async () => {
    try { const { data } = await api.get("/settings/company"); setCompany(data); } catch { /* mantém estado atual */ }
  };
  useEffect(() => { loadCompany(); }, []);

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      const { key: _key, ...body } = company;
      await api.put("/settings/company", body);
      toast.success("Dados da empresa salvos! Serão usados nos contratos.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSavingCompany(false); }
  };
  const setC = (k, v) => setCompany((p) => ({ ...p, [k]: v }));

  const genLink = async () => {
    const { data } = await api.post("/applications/generate-link");
    const url = `${window.location.origin}${data.url}`;
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Link copiado!", { description: url });
  };

  return (
    <div className="space-y-5 fade-up" data-testid="settings-page">
      <div><h1 className="text-2xl font-extrabold tracking-tight">Configurações</h1><p className="text-slate-500 text-sm">Perfil, link público e auditoria.</p></div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("aba") || "perfil"}>
        <TabsList>
          <TabsTrigger value="perfil" data-testid="set-tab-perfil">Perfil</TabsTrigger>
          <TabsTrigger value="link" data-testid="set-tab-link">Link Público</TabsTrigger>
          <TabsTrigger value="notificacoes" data-testid="set-tab-notificacoes">Notificações</TabsTrigger>
          {isAdmin && <TabsTrigger value="empresa" data-testid="set-tab-empresa">Empresa</TabsTrigger>}
          {isAdmin && !isMaster && <TabsTrigger value="assinatura" data-testid="set-tab-assinatura">Assinatura</TabsTrigger>}
          {isAdmin && <TabsTrigger value="mensagens" data-testid="set-tab-mensagens">Mensagens</TabsTrigger>}
          {isAdmin && <TabsTrigger value="contratos" data-testid="set-tab-contratos">Modelos de Contrato</TabsTrigger>}
          {isAdmin && <TabsTrigger value="auditoria" data-testid="set-tab-auditoria">Auditoria</TabsTrigger>}
        </TabsList>

        <TabsContent value="perfil" className="trt-card p-6 mt-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-onyx flex items-center justify-center"><Crown className="w-8 h-8 gold-text" /></div>
            <div>
              <div className="text-xl font-bold">{user?.name}</div>
              <div className="text-slate-500">{user?.email}</div>
              <div className="gold-text text-sm capitalize mt-1">{user?.role}</div>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="w-4 h-4 gold-text" /> Dados protegidos por autenticação, permissões e conformidade LGPD.</div>
          <ChangePassword />
        </TabsContent>

        <TabsContent value="notificacoes" className="trt-card p-6 mt-4">
          <PushConfig />
        </TabsContent>

        <TabsContent value="link" className="trt-card p-6 mt-4">
          <h3 className="font-bold flex items-center gap-2"><Link2 className="w-5 h-5 gold-text" /> Link de Análise de Crédito</h3>
          <p className="text-sm text-slate-500 mt-2 mb-4">Gere um link exclusivo para enviar aos clientes. Eles poderão preencher o pré-cadastro sem precisar de conta.</p>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={genLink} className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black gap-2" data-testid="settings-gen-link"><Link2 className="w-4 h-4" /> Gerar e copiar link</Button>
            <Button variant="outline" disabled={!user?.empresa?.slug} onClick={() => window.open(`/solicitar-credito/${user.empresa.slug}`, "_blank")} data-testid="settings-preview-link">Ver formulário público</Button>
          </div>
          {user?.empresa?.slug && (
            <div className="mt-4 rounded-lg bg-slate-50 border p-3 text-xs space-y-1" data-testid="settings-public-link-box">
              <div className="text-slate-500">Link público fixo da sua empresa:</div>
              <div className="flex items-center gap-2 flex-wrap"><span className="mono font-semibold break-all" data-testid="settings-public-link">{`${window.location.origin}/solicitar-credito/${user.empresa.slug}`}</span>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/solicitar-credito/${user.empresa.slug}`).catch(() => {}); toast.success("Link copiado"); }} data-testid="settings-copy-public-link">Copiar</Button></div>
            </div>
          )}
        </TabsContent>

        {isAdmin && !isMaster && (
          <TabsContent value="assinatura" className="trt-card p-6 mt-4">
            <MinhaAssinatura empresaId={user?.empresa_id} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="empresa" className="trt-card p-6 mt-4">
            <h3 className="font-bold flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 gold-text" /> Dados da Empresa (CREDOR)</h3>
            <p className="text-sm text-slate-500 mb-4">Estas informações preenchem automaticamente o cabeçalho e as partes de todos os contratos gerados.</p>
            {!company ? <Loader2 className="w-6 h-6 animate-spin gold-text" /> : (
              <>
                <div className="grid md:grid-cols-2 gap-3">
                  {COMPANY_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-xs">{label}</Label>
                      <Input value={company[k] || ""} onChange={(e) => setC(k, e.target.value)} data-testid={`company-${k}`} className="mt-1" />
                    </div>
                  ))}
                </div>
                <ResponsaveisConfig company={company} setCompany={setCompany} />
                <EquipeConfig />
                <details className="mt-3 rounded-lg border p-3" data-testid="modelos-details"><summary className="cursor-pointer text-sm font-semibold">Modelos de mensagem ao cliente (WhatsApp manual)</summary><div className="mt-2"><ModelosConfig /></div></details>
                <Button onClick={saveCompany} disabled={savingCompany} className="mt-4 bg-onyx hover:bg-black text-white gap-2" data-testid="company-save-btn">
                  {savingCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Salvar dados da empresa</>}
                </Button>
                <LogoUpload />
                <LogoUpload kind="contrato" />
              </>
            )}
          </TabsContent>
        )}

        {isAdmin && <TabsContent value="mensagens" className="mt-4"><MensagensConfig /></TabsContent>}
        {isAdmin && <TabsContent value="contratos" className="mt-4"><ContratoModelosConfig /></TabsContent>}

        {isAdmin && (
          <TabsContent value="auditoria" className="mt-4"><AuditLog /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
